import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faCopy, faCheck, faCalendarCheck, faIdCard, faPassport, faHeartPulse, faCakeCandles } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import AddTeamMember from './AddTeamMember';
import EditTeamMember from './EditTeamMember';
import ViewTeamMember from './ViewTeamMember';
import { dubaiDate, toDubai, dubaiDaysSince, DUBAI_ZONE } from '../../../utils/dubaiTime';
import './team.css';

const PART_TIME_ROLE = 'part-time cleaner';
const PART_TIME_COLLAPSED_COUNT = 12;

// Whole Dubai days until a document expires: >0 upcoming, 0 today, <0 overdue.
// (dubaiDaysSince is positive in the past, so negate it.)
const daysUntilExpiry = (v) => {
  const since = dubaiDaysSince(v);
  return since === null ? null : -since;
};

// Whole Dubai days until the NEXT occurrence of a birthday (0 = today).
const daysUntilBirthday = (dob) => {
  const d = toDubai(dob);
  if (!d) return null;
  const today = DateTime.now().setZone(DUBAI_ZONE).startOf('day');
  let next = DateTime.fromObject({ year: today.year, month: d.month, day: d.day }, { zone: DUBAI_ZONE }).startOf('day');
  if (!next.isValid) next = DateTime.fromObject({ year: today.year, month: 3, day: 1 }, { zone: DUBAI_ZONE }).startOf('day'); // 29 Feb → 1 Mar
  if (next < today) next = next.plus({ years: 1 });
  return Math.round(next.diff(today, 'days').days);
};

function Team() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMemberId, setEditMemberId] = useState(null);
  const [viewMemberId, setViewMemberId] = useState(null);
  const [expandedPartTimers, setExpandedPartTimers] = useState({ active: false, archived: false });

  const userRole = localStorage.getItem('userRole');
  const navigate = useNavigate();
  // Attendance lives on the Salaries page as a tab. This button deep-links
  // straight to that tab (same page — no duplicate). Managers also reach it
  // from the sidebar's Salaries link; admins & supervisors only from here.
  const canSeeAttendance = ['manager', 'admin', 'supervisor'].includes(String(userRole || '').toLowerCase());

  const roleOrder = ['cleaner', 'driver', 'warehouse', 'part-time cleaner', 'admin', 'supervisor', 'manager'];

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/api/team');
      setTeamMembers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      setError('Error fetching team members');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleAddTeam = () => {
    setShowAddModal(true);
  };

  const handleEditTeam = (id) => {
    setEditMemberId(id);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditMemberId(null);
    setViewMemberId(null);
  };

  // View → Edit hand-off: close the dossier, open the edit modal for the
  // same member.
  const handleViewEdit = () => {
    setEditMemberId(viewMemberId);
    setViewMemberId(null);
  };

  const handleModalSaved = () => {
    closeModals();
    fetchTeamMembers();
  };

  const handleCopy = (key, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1500);
      })
      .catch(err => console.error('Failed to copy:', err));
  };

  const formatRole = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() + 's';
  };

  const formatRoleSingular = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  // Asia/Dubai render (item 8) — same dd/mm/yyyy visual as before.
  const formatDate = (dateString) => dubaiDate(dateString);

  const groupMembersByRole = (members) => {
    const grouped = members.reduce((acc, member) => {
      const rawRole = member.role_name || 'unknown';
      const role = rawRole.toLowerCase();

      if (!acc[role]) {
        acc[role] = [];
      }
      acc[role].push(member);
      return acc;
    }, {});

    return roleOrder.reduce((acc, role) => {
      if (grouped[role]) {
        acc[role] = grouped[role];
      }
      return acc;
    }, {});
  };

  // last_used_at DESC, members never assigned to a booking last
  const sortByLastUsed = (members) => (
    [...members].sort((a, b) => {
      if (!a.last_used_at && !b.last_used_at) return 0;
      if (!a.last_used_at) return 1;
      if (!b.last_used_at) return -1;
      return new Date(b.last_used_at) - new Date(a.last_used_at);
    })
  );

  // Icon-only (the owner: no "Copy" text so it doesn't take up space) — the icon
  // flips to a tick while copied.
  const renderCopyChip = (key, value) => (
    <button
      type="button"
      className={`copy-chip${copiedKey === key ? ' copied' : ''}`}
      onClick={(e) => { e.stopPropagation(); handleCopy(key, value); }}
      title={copiedKey === key ? 'Copied' : 'Copy to clipboard'}
      aria-label={copiedKey === key ? 'Copied' : 'Copy to clipboard'}
    >
      <FontAwesomeIcon icon={copiedKey === key ? faCheck : faCopy} />
    </button>
  );

  const renderContactRow = (member, label, field, labelClass = '', asWhatsAppLink = false) => {
    const value = member[field];
    return (
      <div className="contact-row">
        <span className={`contact-label ${labelClass}`.trim()}>{label}</span>
        {value ? (
          <>
            {asWhatsAppLink ? (
              <a
                className="contact-value whatsapp-link"
                href={`https://wa.me/${value.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                title="Open WhatsApp chat"
                onClick={(e) => e.stopPropagation()}
              >
                {value}
              </a>
            ) : (
              <span className="contact-value">{value}</span>
            )}
            {renderCopyChip(`${member.id}:${field}`, value)}
          </>
        ) : (
          <span className="contact-empty">—</span>
        )}
      </div>
    );
  };

  // The same rule whether the member is active or ARCHIVED (owner,
  // 2026-10-12: "supervisors and admins should be able to edit and unarchive
  // old team members" — the pencil used to be manager-only on archived
  // cards, so they could not reach the Unarchive button at all). Admins and
  // supervisors handle everyone below supervisor; managers handle everyone
  // but managers. The server hides managers from non-managers regardless.
  const canEditMember = (role) => {
    if (userRole === 'manager') return role !== 'manager';
    if (userRole === 'admin' || userRole === 'supervisor') return role !== 'manager' && role !== 'supervisor';
    return false;
  };

  const renderStaffTag = (member) => {
    if (member.is_trial) return <span className="staff-tag staff-tag-trial">Trial staff</span>;
    if (member.does_have_id_from_company) return <span className="staff-tag staff-tag-company-id">Company ID</span>;
    if (member.does_have_id) return <span className="staff-tag staff-tag-has-id">Has ID</span>;
    return null;
  };

  const renderMemberCard = (member, role, archived) => (
    <li key={member.id} className="team-member">
      {/* Whole card opens the read-only dossier; inner controls (copy chips,
          wa.me link, edit) stopPropagation so they still work. */}
      <div
        className={`member-card member-card-clickable${archived ? ' archived-member-card' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => setViewMemberId(member.id)}
        onKeyDown={(e) => {
          // Only when the card itself is focused — Enter on an inner button
          // bubbles here and must not reopen the view.
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setViewMemberId(member.id);
          }
        }}
        title="View team member"
      >
        <div className="member-card-head">
          <div className="member-identity">
            <h4>{`${member.first_name} ${member.last_name}`}</h4>
            <div className="member-badges">
              <span className="role-badge">{formatRoleSingular(role)}</span>
              <span className={`status-badge ${archived ? 'status-archived' : 'status-active'}`}>
                {archived ? 'Archived' : 'Active'}
              </span>
              {renderStaffTag(member)}
            </div>
          </div>
          {canEditMember(role, archived) && (
            <div className="member-card-actions">
              {/* Unarchive lives in the EDIT modal footer (replaces Delete
                  for archived members) — no button on the card itself. */}
              <button
                type="button"
                className="member-edit-button"
                onClick={(e) => { e.stopPropagation(); handleEditTeam(member.id); }}
                title="Edit team member"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            </div>
          )}
        </div>

        {!archived && Array.isArray(member.company_id_missing) && member.company_id_missing.length > 0 && (
          <div className="cid-alert" title={`Missing for Company ID: ${member.company_id_missing.join(', ')}`}>
            <strong>⚠ Company ID — {member.company_id_missing.length} missing:</strong>{' '}
            {member.company_id_missing.slice(0, 2).join(', ')}
            {member.company_id_missing.length > 2 ? ` +${member.company_id_missing.length - 2} more` : ''}
          </div>
        )}

        <div className="member-contacts">
          {renderContactRow(member, 'WhatsApp', 'phone_number', 'contact-label-whatsapp', true)}
          {renderContactRow(member, '📞 Calling', 'calling_number')}
        </div>

        <div className="member-meta">
          {renderContactRow(member, 'EID', 'eid')}
          <p className="member-joined">Joined {formatDate(member.created_at)}</p>
        </div>
      </div>
    </li>
  );

  const renderRoleGroups = (grouped, archived) => (
    Object.keys(grouped).map((role) => {
      const totalCount = grouped[role].length;
      const sectionKey = archived ? 'archived' : 'active';
      // Search must always cover everyone, so collapse only without a query
      const collapsible = role === PART_TIME_ROLE && !searchQuery;

      let members = grouped[role];
      if (collapsible) {
        members = sortByLastUsed(members);
        if (!expandedPartTimers[sectionKey]) {
          members = members.slice(0, PART_TIME_COLLAPSED_COUNT);
        }
      }

      return (
        <div key={role} className="role-group">
          <h3>
            {formatRole(role)}
            <span className="role-count">{totalCount}</span>
          </h3>
          <ul className="team-list">
            {members.map((member) => renderMemberCard(member, role, archived))}
          </ul>
          {collapsible && totalCount > PART_TIME_COLLAPSED_COUNT && (
            <button
              type="button"
              className="show-all-button"
              onClick={() => setExpandedPartTimers((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
            >
              {expandedPartTimers[sectionKey] ? 'Show less' : `Show all ${totalCount} part-time cleaners`}
            </button>
          )}
        </div>
      );
    })
  );

  // Top-of-page alert strip: soonest-expiring documents + upcoming birthdays,
  // across ACTIVE staff only. Each card shows the next 3; rows open the dossier.
  const renderTopWidgets = () => {
    const active = teamMembers.filter((m) => m.is_active !== false);
    const topExpiry = (field) => active
      .map((m) => ({ m, days: daysUntilExpiry(m[field]), raw: m[field] }))
      .filter((x) => x.raw && x.days !== null)
      .sort((a, b) => a.days - b.days)
      .slice(0, 3);
    const labour = topExpiry('work_permit_expiry_date');
    const passport = topExpiry('passport_expiry_date');
    const health = topExpiry('health_insurance_expiry');
    const birthdays = active
      .map((m) => ({ m, days: daysUntilBirthday(m.date_of_birth), raw: m.date_of_birth }))
      .filter((x) => x.raw && x.days !== null)
      .sort((a, b) => a.days - b.days)
      .slice(0, 3);

    const nm = (m) => `${m.first_name || ''} ${m.last_name || ''}`.trim();
    const expiryLabel = (d) => (d < 0 ? `${-d}d overdue` : d === 0 ? 'Today' : `${d}d left`);
    const expiryTone = (d) => (d < 0 ? 'is-overdue' : d <= 30 ? 'is-soon' : '');
    const bdayLabel = (d) => (d === 0 ? 'Today 🎉' : `in ${d}d`);

    const card = (icon, title, rows, kind) => (
      <div className="tw-card">
        <div className="tw-card-head">
          <span className={`tw-icon tw-icon-${kind}`}><FontAwesomeIcon icon={icon} /></span>
          <h4>{title}</h4>
        </div>
        {rows.length === 0 ? (
          <div className="tw-empty">None on file</div>
        ) : (
          <ul className="tw-list">
            {rows.map(({ m, days, raw }) => (
              <li key={m.id} className="tw-row" role="button" tabIndex={0}
                onClick={() => setViewMemberId(m.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewMemberId(m.id); } }}>
                <span className="tw-name" title={nm(m)}>{nm(m)}</span>
                {kind === 'bday'
                  ? <span className="tw-when tw-bday" title={dubaiDate(raw)}>{bdayLabel(days)}</span>
                  : <span className={`tw-when ${expiryTone(days)}`} title={dubaiDate(raw)}>{expiryLabel(days)}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    return (
      <div className="team-widgets">
        {card(faIdCard, 'Labour Card expiring', labour, 'labour')}
        {card(faPassport, 'Passport expiring', passport, 'passport')}
        {card(faHeartPulse, 'Health Insurance expiring', health, 'health')}
        {card(faCakeCandles, 'Upcoming birthdays', birthdays, 'bday')}
      </div>
    );
  };

  if (loading) {
    return <div>Loading team members...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const filteredTeamMembers = teamMembers.filter(member => {
    const first = (member.first_name || '').toLowerCase();
    const last = (member.last_name || '').toLowerCase();
    return first.includes(searchQuery) || last.includes(searchQuery);
  });

  const activeMembers = filteredTeamMembers.filter((m) => m.is_active !== false);
  const archivedMembers = filteredTeamMembers.filter((m) => m.is_active === false);

  const groupedByRole = groupMembersByRole(activeMembers);
  const groupedArchivedByRole = groupMembersByRole(archivedMembers);

  const hasNoMembers = activeMembers.length === 0 && archivedMembers.length === 0;

  // Edit button inside the view modal follows the same permission rules as
  // the card's edit icon.
  const viewMember = viewMemberId !== null
    ? teamMembers.find((m) => m.id === viewMemberId)
    : null;
  const canEditViewMember = viewMember
    ? canEditMember((viewMember.role_name || '').toLowerCase(), viewMember.is_active === false)
    : false;

  return (
    <div className="team-container">
      <div className="header-row">
        <h2>Team Members</h2>
        <div className="search-add-container">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
          {canSeeAttendance && (
            <button className="attendance-button" onClick={() => navigate('/salaries?tab=attendance')} title="Attendance & Leaves">
              <FontAwesomeIcon icon={faCalendarCheck} /> Attendance &amp; Leaves
            </button>
          )}
          <button className="add-button" onClick={handleAddTeam}>+ Add Team Member</button>
        </div>
      </div>

      {renderTopWidgets()}

      {hasNoMembers ? (
        <p>No team members found.</p>
      ) : (
        <>
          {renderRoleGroups(groupedByRole, false)}

          {archivedMembers.length > 0 && (
            <div className="archived-section">
              <h2 style={{ marginTop: '2rem' }}>Archived Team Members</h2>
              {renderRoleGroups(groupedArchivedByRole, true)}
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <div className="team-modal-overlay">
          <div className="team-modal">
            <div className="team-modal-header">
              <h3>Add Team Member</h3>
              <button type="button" className="team-modal-close" onClick={closeModals} title="Close">×</button>
            </div>
            <div className="team-modal-body">
              <AddTeamMember onClose={closeModals} onSaved={handleModalSaved} />
            </div>
          </div>
        </div>
      )}

      {viewMemberId !== null && (
        <ViewTeamMember
          memberId={viewMemberId}
          onClose={closeModals}
          onEdit={canEditViewMember ? handleViewEdit : undefined}
        />
      )}

      {editMemberId !== null && (
        <div className="team-modal-overlay">
          <div className="team-modal">
            <div className="team-modal-header">
              <h3>Edit Team Member</h3>
              <button type="button" className="team-modal-close" onClick={closeModals} title="Close">×</button>
            </div>
            <div className="team-modal-body">
              <EditTeamMember memberId={editMemberId} onClose={closeModals} onSaved={handleModalSaved} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Team;
